/**
 * Wager races — the operator surface (P6, ADR-063).
 *
 * Own module per the B3 convention; shapes mirror
 * `server/src/promo/races.admin.routes.ts`. Money figures are INR-authored
 * decimal strings; prizes are a rank-indexed array (index 0 = first place).
 * Settlement pays through the promo/wallet path — nothing here moves money;
 * voiding records a fact, it deletes nothing.
 */
import { http } from './http.ts';
import { MOCK_MODE } from './client.ts';

export const RACE_METRICS = ['turnover', 'weighted_turnover'] as const;

export type RaceMetric = (typeof RACE_METRICS)[number];

export type RaceState = 'scheduled' | 'running' | 'settled' | 'voided';

export interface RaceView {
  id: number;
  name: string;
  metric: string;
  minWager: string;
  /** Prize schedule by rank, INR-authored; index 0 = first place. */
  prizes: string[];
  state: RaceState;
  startsAt: string;
  endsAt: string;
  settledAt: string | null;
  createdAt: string;
}

export interface RaceResultView {
  id: number;
  raceId: number;
  userId: number;
  rank: number;
  score: string;
  prize: string;
  currency: string;
  createdAt: string;
}

export interface RaceCreate {
  name: string;
  metric: RaceMetric;
  minWager: string;
  prizes: string[];
  startsAt: string;
  endsAt: string;
}

/** LIVE — `GET /internal/admin/races`. Newest first. */
export async function fetchRaces(): Promise<RaceView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ races: RaceView[] }>('/internal/admin/races');
  return body.races;
}

/** LIVE — `GET /internal/admin/races/:id/results`. The paid standings of a settled race. */
export async function fetchRaceResults(id: number): Promise<RaceResultView[]> {
  if (MOCK_MODE) return [];
  const body = await http.get<{ results: RaceResultView[] }>(`/internal/admin/races/${id}/results`);
  return body.results;
}

/** LIVE — `POST /internal/admin/races`. Admin, audited (`race.create`). */
export async function createRace(input: RaceCreate): Promise<RaceView> {
  const body = await http.post<{ race: RaceView }>('/internal/admin/races', input);
  return body.race;
}

/** LIVE — `PATCH /internal/admin/races/:id`. Scheduled races only; admin, audited. */
export async function updateRace(id: number, patch: Partial<RaceCreate>): Promise<RaceView> {
  const body = await http.patch<{ race: RaceView }>(`/internal/admin/races/${id}`, patch);
  return body.race;
}

/** LIVE — `POST /internal/admin/races/:id/void`. No results, no payments; a recorded fact. */
export async function voidRace(id: number): Promise<RaceView> {
  const body = await http.post<{ race: RaceView }>(`/internal/admin/races/${id}/void`);
  return body.race;
}
